
import { useEffect, useState, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TrainingFormValues } from '../../../types';

interface UseUnsavedChangesProps {
  form: UseFormReturn<TrainingFormValues>;
  isSubmitting: boolean;
}

export const useUnsavedChanges = ({ form, isSubmitting }: UseUnsavedChangesProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const hasUserInteractedRef = useRef(false);

  // Track user interaction more efficiently using form dirty state
  useEffect(() => {
    if (isSubmitting) return;
    
    // Use form's built-in dirty state instead of JSON comparison
    const isDirty = form.formState.isDirty;
    
    if (isDirty && hasUserInteractedRef.current) {
      setHasUnsavedChanges(true);
    } else if (!isDirty) {
      setHasUnsavedChanges(false);
    }
  }, [form.formState.isDirty, isSubmitting]);

  // Track user interaction with form fields (debounced)
  useEffect(() => {
    const formElement = document.querySelector('form');
    if (!formElement) return;

    let timeoutId: NodeJS.Timeout;

    const handleUserInteraction = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        hasUserInteractedRef.current = true;
      }, 100); // Small debounce to prevent excessive calls
    };

    formElement.addEventListener('input', handleUserInteraction);
    formElement.addEventListener('change', handleUserInteraction);
    formElement.addEventListener('keydown', handleUserInteraction);

    return () => {
      clearTimeout(timeoutId);
      formElement.removeEventListener('input', handleUserInteraction);
      formElement.removeEventListener('change', handleUserInteraction);
      formElement.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Clear unsaved changes flag after successful submission
  useEffect(() => {
    if (isSubmitting) {
      setHasUnsavedChanges(false);
      hasUserInteractedRef.current = false;
    }
  }, [isSubmitting]);

  const resetUnsavedChanges = () => {
    setHasUnsavedChanges(false);
    hasUserInteractedRef.current = false;
  };

  return {
    hasUnsavedChanges,
    resetUnsavedChanges
  };
};
